import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listCycleCounts } from "@/lib/cycle-counts.functions";
import { RfShell, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { useScope } from "@/hooks/use-scope";
import { useAuth } from "@/hooks/use-auth";
import { ChevronRight, Hand, Lock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/rf/cycle-count")({
  component: RfCycleCounts,
});

function RfCycleCounts() {
  const { tenantId, facilityId } = useScope();
  const { user } = useAuth();
  const userId = user?.id;
  const listFn = useServerFn(listCycleCounts);
  const { data: counts = [], isLoading } = useQuery({
    queryKey: ["rf-cycle-counts", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () =>
      listFn({ data: { tenantId: tenantId!, facilityId: facilityId!, status: "open" } }),
    refetchInterval: 15_000,
  });

  if (!tenantId || !facilityId) {
    return (
      <RfShell title="Cycle Count" back>
        <RfScopeMissing />
      </RfShell>
    );
  }

  return (
    <RfShell title="Cycle Count" back>
      <div className="text-xs text-muted-foreground px-1">
        {isLoading ? "Loading…" : `${counts.length} open counts`}
      </div>
      <div className="space-y-2">
        {counts.length === 0 && !isLoading ? (
          <RfEmpty>No open cycle counts. Create one from desktop.</RfEmpty>
        ) : (
          (counts as any[])
            // Hide counts another worker is actively counting; show mine
            // and unclaimed. Mirrors replenishment/putaway worker mental
            // model: you only see what you can act on.
            .filter((c) => !c.claimed_by || c.claimed_by === userId)
            .map((c) => {
              const mine = c.claimed_by && c.claimed_by === userId;
              return (
                <Link
                  key={c.id}
                  to="/app/rf/cycle-count/$id"
                  params={{ id: c.id }}
                  className="flex items-center gap-3 rounded-md border bg-card px-3 py-3 active:bg-muted/70"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      {mine ? (
                        <><Hand className="h-3 w-3 text-emerald-600" /> yours · resume</>
                      ) : (
                        <>Created {new Date(c.created_at).toLocaleDateString()}</>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </Link>
              );
            })
        )}
      </div>
    </RfShell>
  );
}